import { memo, useEffect, useState } from "react";
import {
  Badge,
  Box,
  CircularProgress,
  Link,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { groupBy, orderBy } from "lodash";

import { DESIGN_DETAILS } from "helpers/apis";
import useAxios from "helpers/axios";
import useAuth from "hooks/useAuth";
import useCatalogDetail from "hooks/useCatalogDetail";
import { colors } from "theme";
import { CnvStatus, CnvToggleStatus, IDesign, Workflow } from "types/design";
import { IDesignDetail } from "types/designDetail";

import Footnotes from "./Footnotes";
import GroupRow from "./TableRow";

const useStyles = makeStyles((theme: Theme) => ({
  tableContainer: {
    background: "#FFF",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  specialModule: {
    padding: 16,
    "& > h6": {
      fontWeight: 400,
      fontSize: 12,
      lineHeight: 1.5,
      color: colors.nero[300],
    },
  },
  caption: {
    padding: "0 !important",
    color: "inherit !important",
  },
  header: {
    "& > th.MuiTableCell-head": {
      backgroundColor: "#FFF",
      padding: "11px 13px 11px 16px",
      fontSize: 12,
      lineHeight: "18px",
      letterSpacing: 0.5,
      fontWeight: 600,
      borderBottom: "none",
      whiteSpace: "nowrap",
    },
  },
  badge: {
    marginTop: 16,
    marginLeft: 6,
  },
  // Anonymous User
  noLoginList: {
    display: "grid",
    width: "100%",
    columnGap: 16,
    rowGap: 16,
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "3fr 3fr 3fr 3fr",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "3fr 3fr 3fr",
    },
  },
  noLoginListItem: {
    background: colors.grey[700],
    borderRadius: 4,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: "17px",
    letterSpacing: 0.5,
    padding: "8.5px 8px",
  },
}));

const SpecialModuleRow = ({
  hasMsi = false,
  hasTmb = false,
}: {
  hasMsi?: boolean;
  hasTmb?: boolean;
}) => {
  const classes = useStyles();

  return (
    <Box mb={2} className={`default-bg ${classes.specialModule}`}>
      <Typography variant="subtitle2">
        {hasTmb
          ? "Tumor Mutational Burden (TMB)"
          : hasMsi
          ? "Microsatellite Instability (MSI)"
          : "SNPID for Sample Tracking (SNPs can be used in combination to uniquely tag and track samples over time)"}
      </Typography>
    </Box>
  );
};

const DesignDetailTable = ({ workflow }: { workflow: Workflow }) => {
  const { isLoggedIn } = useAuth();
  let { catalog, tmbToggle, isCustomPanel } = useCatalogDetail();
  catalog = catalog as IDesign;
  const [details, setDetails] = useState<IDesignDetail[]>([]);
  const [detailGroups, setDetailGroups] = useState<IDesignDetail[][]>([]);
  const classes = useStyles();
  const { loading } = useAxios({
    config: {
      url: DESIGN_DETAILS,
      params: {
        // @todo Need to define a custom panel type and replace it with that type
        designId: isCustomPanel
          ? ((catalog as any).panelspresent as Array<any>)
              .map((item: any) => item.design)
              .join()
          : catalog.id,
      },
    },
    successCallback: ({ response }: any) => {
      if (response !== null && Array.isArray(response.data)) {
        setDetails(response.data);
      } else {
        setDetails([]);
      }
    },
  });

  useEffect(() => {
    setDetailGroups(
      Object.values(groupBy(orderBy(details, ["gene"], ["asc"]), "gene"))
    );
  }, [details]);

  const MsiRow = () => {
    if (
      [Workflow.VARITANT, Workflow.FUSION].includes(workflow) &&
      ((!!catalog?.hasMsi && catalog?.msiToggle === CnvToggleStatus.MUTABLE) ||
        catalog?.msiToggle === CnvToggleStatus.IMMUTABLE_YES)
    ) {
      return <SpecialModuleRow hasMsi />;
    }
    return null;
  };

  const SnpIdRow = () => {
    if (
      [Workflow.VARITANT, Workflow.FUSION].includes(workflow) &&
      ((!!catalog?.hasSnpId &&
        catalog?.snpIdToggle === CnvToggleStatus.MUTABLE) ||
        catalog?.snpIdToggle === CnvToggleStatus.IMMUTABLE_YES)
    ) {
      return <SpecialModuleRow />;
    }
    return null;
  };

  const TmbRow = () => {
    if (Workflow.VARITANT === workflow && (!!catalog?.hasTmb || tmbToggle)) {
      return <SpecialModuleRow hasTmb />;
    }
    return null;
  };

  if (!isLoggedIn) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        width={1}
        padding={2}
        boxSizing="border-box"
      >
        <TmbRow />
        <MsiRow />
        <SnpIdRow />
        <List className={classes.noLoginList}>
          {detailGroups.map((detailGroup: IDesignDetail[]) => {
            return (
              <ListItem
                key={detailGroup[0].id}
                className={classes.noLoginListItem}
              >
                {detailGroup[0].gene}
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  }

  return (
    <TableContainer className={`${classes.tableContainer} design-detail-table`}>
      <TmbRow />
      <MsiRow />
      <SnpIdRow />
      <Table stickyHeader>
        <caption className={classes.caption}>
          <Footnotes workflow={workflow} designType={catalog.designType} />
        </caption>
        <TableHead>
          <TableRow className={classes.header}>
            <TableCell>Gene</TableCell>
            <TableCell>Accession</TableCell>
            <TableCell>
              <Badge
                badgeContent={
                  <Link color="secondary" className={classes.badge}>
                    *
                  </Link>
                }
                invisible={workflow !== Workflow.FUSION}
              >
                Exon
              </Badge>
            </TableCell>
            {workflow === Workflow.FUSION && (
              <>
                <TableCell>Assay Type</TableCell>
                <TableCell>
                  Description<Link color="secondary">†</Link>
                </TableCell>
              </>
            )}
            {workflow !== Workflow.FUSION && (
              <TableCell>
                <Badge
                  badgeContent={
                    <Link color="secondary" className={classes.badge}>
                      *
                    </Link>
                  }
                >
                  CNV
                </Badge>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            detailGroups.map((detailGroup: IDesignDetail[], index) => {
              // Get a customized cnv value for custom panel
              let cnv: boolean = detailGroup[0].cnv === CnvStatus.YES;
              if (isCustomPanel) {
                cnv =
                  (catalog as any).panelspresent.find(
                    (panel: any) => panel.design === detailGroup[0].design
                  )?.cnv || false;
              }

              return (
                <GroupRow
                  key={index}
                  detailGroup={detailGroup}
                  workflow={workflow}
                  cnv={cnv}
                />
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default memo(DesignDetailTable);
